import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  TwoWheeler as RiderIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MonetizationOn as CashIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';

const Riders = () => {
  const { actions } = useApp();
  const [riders, setRiders] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newRider, setNewRider] = useState({
    mobileNo: '',
    f_name: '',
    storeId: ''
  });
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [existingUser, setExistingUser] = useState(null);
  const [riderDetailsOpen, setRiderDetailsOpen] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderCashHolding, setRiderCashHolding] = useState(0);
  const [cashCollections, setCashCollections] = useState([]);
  const [cashCollectionsLoading, setCashCollectionsLoading] = useState(false);
  const [collectingCash, setCollectingCash] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [otpGenerated, setOtpGenerated] = useState(false);
  const [generatingOtp, setGeneratingOtp] = useState(false);

  // Fetch riders data
  const fetchRiders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${appConfig.api.baseURL}/users/riders/list?search=${searchTerm}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setRiders(data.riders || []);
      } else {
        actions.showSnackbar('Failed to fetch riders', 'error');
      }
    } catch (error) {
      console.error('Error fetching riders:', error);
      actions.showSnackbar('Error fetching riders', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stores data
  const fetchStores = async () => {
    try {
      const response = await fetch(`${appConfig.api.baseURL}/stores`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setStores(data.stores || []);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  // Check if user exists by mobile number
  const checkUserExists = async (mobileNo) => {
    if (!mobileNo || mobileNo.length < 10) {
      setUserExists(null);
      setExistingUser(null);
      return;
    }

    setCheckingUser(true);
    try {
      const response = await fetch(`${appConfig.api.baseURL}/users/${mobileNo}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setUserExists(true);
        setExistingUser(data.user);
      } else {
        setUserExists(false);
        setExistingUser(null);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      setUserExists(false);
      setExistingUser(null);
    } finally {
      setCheckingUser(false);
    }
  };

  // Create or update rider
  const handleCreateRider = async () => {
    if (!newRider.mobileNo) {
      actions.showSnackbar('Mobile number is required', 'error');
      return;
    }

    // For existing users, we don't need f_name in the request
    // For new users, f_name is required
    if (userExists !== true && !newRider.f_name) {
      actions.showSnackbar('Full name is required for new users', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare the request payload
      const requestPayload = {
        mobileNo: newRider.mobileNo,
        storeId: newRider.storeId || null
      };

      // Only include f_name for new users
      if (userExists !== true && newRider.f_name) {
        requestPayload.f_name = newRider.f_name;
      }

      const response = await fetch(`${appConfig.api.baseURL}/users/riders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify(requestPayload)
      });
      
      const data = await response.json();
      if (data.success) {
        actions.showSnackbar(data.message, 'success');
        setOpenAddDialog(false);
        setNewRider({ mobileNo: '', f_name: '', storeId: '' });
        setUserExists(null);
        setExistingUser(null);
        fetchRiders();
      } else {
        actions.showSnackbar(data.message || 'Failed to create rider', 'error');
      }
    } catch (error) {
      console.error('Error creating rider:', error);
      actions.showSnackbar('Error creating rider', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle mobile number change
  const handleMobileChange = (e) => {
    const mobileNo = e.target.value;
    setNewRider(prev => ({ ...prev, mobileNo }));
    
    // Check user existence after a short delay
    setTimeout(() => {
      checkUserExists(mobileNo);
    }, 500);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setNewRider({ mobileNo: '', f_name: '', storeId: '' });
    setUserExists(null);
    setExistingUser(null);
  };

  // Handle search
  const handleSearch = () => {
    fetchRiders();
  };

  // Handle view rider details
  const handleViewRiderDetails = async (rider) => {
    setSelectedRider(rider);
    setRiderDetailsOpen(true);
    setCashCollections([]);

    // Fetch rider's cash holding
    try {
      const response = await fetch(`${appConfig.api.baseURL}/users/${rider.userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setRiderCashHolding(data.user?.cashHolding || 0);
      }
    } catch (error) {
      console.error('Error fetching rider cash holding:', error);
      setRiderCashHolding(0);
    }

    // Fetch recent cash collections for this rider
    setCashCollectionsLoading(true);
    try {
      const params = new URLSearchParams({
        deliveryPersonId: rider.userId,
        limit: '20',
        page: '1'
      });
      const collResponse = await fetch(`${appConfig.api.baseURL}/cash-collections?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });
      const collData = await collResponse.json();
      if (collData.success && collData.data?.cashCollections) {
        setCashCollections(collData.data.cashCollections);
      }
    } catch (error) {
      console.error('Error fetching cash collections:', error);
      setCashCollections([]);
    } finally {
      setCashCollectionsLoading(false);
    }
  };

  // Generate OTP for cash collection
  const handleGenerateOtp = async () => {
    if (!selectedRider?.mobileNo) return;

    setGeneratingOtp(true);
    try {
      const response = await fetch(`${appConfig.api.baseURL}/cash-collections/generate-submit-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          mobileNo: selectedRider.mobileNo
        })
      });

      const data = await response.json();
      if (data.success) {
        setGeneratedOtp(data.data.otp);
        setOtpGenerated(true);
        actions.showSnackbar('OTP generated successfully', 'success');
      } else {
        actions.showSnackbar(data.message || 'Failed to generate OTP', 'error');
      }
    } catch (error) {
      console.error('Error generating OTP:', error);
      actions.showSnackbar('Error generating OTP', 'error');
    } finally {
      setGeneratingOtp(false);
    }
  };

  // Handle collect cash
  const handleCollectCash = async () => {
    if (!selectedRider || riderCashHolding <= 0) return;

    setCollectingCash(true);
    try {
      const response = await fetch(`${appConfig.api.baseURL}/cash-collections/collect-cash`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          riderId: selectedRider.userId,
          amount: riderCashHolding
        })
      });

      const data = await response.json();
      if (data.success) {
        actions.showSnackbar(`Successfully collected ₹${riderCashHolding} from ${selectedRider.f_name}`, 'success');
        setRiderCashHolding(0);
        setRiderDetailsOpen(false);
        setSelectedRider(null);
        setGeneratedOtp('');
        setOtpGenerated(false);
      } else {
        actions.showSnackbar(data.message || 'Failed to collect cash', 'error');
      }
    } catch (error) {
      console.error('Error collecting cash:', error);
      actions.showSnackbar('Error collecting cash', 'error');
    } finally {
      setCollectingCash(false);
    }
  };

  // Handle close rider details
  const handleCloseRiderDetails = () => {
    setRiderDetailsOpen(false);
    setSelectedRider(null);
    setRiderCashHolding(0);
    setCashCollections([]);
    setGeneratedOtp('');
    setOtpGenerated(false);
  };

  const formatCashCollectionDate = (timestamp) => {
    if (timestamp == null) return '—';
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp?._seconds != null) {
      date = new Date(timestamp._seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  };

  // Load data on component mount
  useEffect(() => {
    fetchRiders();
    fetchStores();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RiderIcon />
          Delivery Riders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddDialog(true)}
          sx={{ borderRadius: 2 }}
        >
          Add New Rider
        </Button>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name or mobile number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{ borderRadius: 2 }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={fetchRiders}
              startIcon={<RefreshIcon />}
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Riders Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rider Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Mobile Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assigned Store</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : riders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No riders found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              riders.map((rider) => (
                <TableRow key={rider.userId} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {rider.f_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{rider.mobileNo}</TableCell>
                  <TableCell>
                    {rider.storeDetails ? (
                      <Typography variant="body2">
                        {rider.storeDetails.name || rider.storeDetails.storeName}
                      </Typography>
                    ) : (
                      <Chip label="Not Assigned" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={rider.isRiderActive ? "Active" : "Inactive"} 
                      size="small" 
                      color={rider.isRiderActive ? "success" : "default"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewRiderDetails(rider)}
                      sx={{ borderRadius: 2 }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Rider Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Rider</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Mobile Number"
              value={newRider.mobileNo}
              onChange={handleMobileChange}
              placeholder="Enter mobile number"
              sx={{ mb: 2 }}
              disabled={loading}
            />
            
            {checkingUser && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Checking user...</Typography>
              </Box>
            )}

            {userExists === true && existingUser && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>User Found:</strong> {existingUser.f_name} ({existingUser.mobileNo})
                </Typography>
                <Typography variant="body2">
                  This user will be marked as a rider.
                </Typography>
              </Alert>
            )}

            {userExists === false && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  User not found. A new user will be created.
                </Typography>
              </Alert>
            )}

            {userExists === true && existingUser ? (
              <TextField
                fullWidth
                label="Full Name"
                value={existingUser.f_name}
                disabled
                sx={{ mb: 2 }}
              />
            ) : (
              <TextField
                fullWidth
                label="Full Name"
                value={newRider.f_name}
                onChange={(e) => setNewRider(prev => ({ ...prev, f_name: e.target.value }))}
                placeholder="Enter full name"
                sx={{ mb: 2 }}
                disabled={loading}
              />
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assigned Store (Optional)</InputLabel>
              <Select
                value={newRider.storeId}
                onChange={(e) => setNewRider(prev => ({ ...prev, storeId: e.target.value }))}
                label="Assigned Store (Optional)"
                disabled={loading}
              >
                <MenuItem value="">
                  <em>No Store Assigned</em>
                </MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name || store.storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateRider} 
            variant="contained" 
            disabled={loading || !newRider.mobileNo || (!existingUser && !newRider.f_name)}
          >
            {loading ? <CircularProgress size={20} /> : userExists === true ? 'Mark as Rider' : 'Create Rider'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rider Details Modal */}
      <Dialog open={riderDetailsOpen} onClose={handleCloseRiderDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RiderIcon color="primary" />
            Rider Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRider && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Rider Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Personal Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRider.f_name}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Mobile Number
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRider.mobileNo}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip 
                      label={selectedRider.isRiderActive ? "Active" : "Inactive"} 
                      size="small" 
                      color={selectedRider.isRiderActive ? "success" : "default"} 
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Assigned Store
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRider.storeDetails ? 
                        (selectedRider.storeDetails.name || selectedRider.storeDetails.storeName) : 
                        'Not Assigned'
                      }
                    </Typography>
                  </Box>
                </Grid>

                {/* Cash Holding Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Cash Information
                  </Typography>
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'grey.50', 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2, 
                    textAlign: 'center',
                    mb: 2
                  }}>
                    <CashIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="text.primary">
                      ₹{riderCashHolding.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current Cash Holding
                    </Typography>
                  </Box>
                  
                  {riderCashHolding > 0 && !otpGenerated && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={handleGenerateOtp}
                      disabled={generatingOtp}
                      sx={{ borderRadius: 2, mb: 2 }}
                    >
                      {generatingOtp ? 'Generating OTP...' : 'Generate OTP'}
                    </Button>
                  )}

                  {otpGenerated && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: 'primary.light', 
                        borderRadius: 2, 
                        textAlign: 'center',
                        mb: 2
                      }}>
                        <Typography variant="h3" fontWeight="bold" color="primary.dark">
                          {generatedOtp}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Give this OTP to the rider
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="success"
                        size="large"
                        fullWidth
                        startIcon={<CashIcon />}
                        onClick={handleCollectCash}
                        disabled={collectingCash}
                        sx={{ borderRadius: 2 }}
                      >
                        {collectingCash ? 'Collecting...' : `Collect Cash (₹${riderCashHolding.toFixed(2)})`}
                      </Button>
                    </Box>
                  )}

                  {riderCashHolding === 0 && (
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'grey.100', 
                      borderRadius: 2, 
                      textAlign: 'center' 
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        No cash to collect
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>

              {/* Recent Cash Collections */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Recent Cash Collections
                </Typography>
                {cashCollectionsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : cashCollections.length === 0 ? (
                  <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">
                      No cash collection transactions yet
                    </Typography>
                  </Box>
                ) : (() => {
                  const byRoute = cashCollections.reduce((acc, tx) => {
                    const key = tx.routeId ?? '__no_route__';
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(tx);
                    return acc;
                  }, {});
                  const routeKeys = Object.keys(byRoute).sort((a, b) => {
                    const aDate = byRoute[a][0]?.paymentCollectedAt ?? byRoute[a][0]?.collectedAt ?? byRoute[a][0]?.createdAt ?? 0;
                    const bDate = byRoute[b][0]?.paymentCollectedAt ?? byRoute[b][0]?.collectedAt ?? byRoute[b][0]?.createdAt ?? 0;
                    const aVal = typeof aDate === 'number' ? aDate : aDate?._seconds != null ? aDate._seconds * 1000 : 0;
                    const bVal = typeof bDate === 'number' ? bDate : bDate?._seconds != null ? bDate._seconds * 1000 : 0;
                    return bVal - aVal;
                  });
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {routeKeys.map((routeKey) => {
                        const routeId = routeKey === '__no_route__' ? null : routeKey;
                        const rows = byRoute[routeKey];
                        const routeTotal = rows.reduce((sum, tx) => sum + (tx.amount != null ? Number(tx.amount) : 0), 0);
                        return (
                          <Box key={routeKey}>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>
                              Route: {routeId ?? '—'}
                              <Typography component="span" variant="subtitle1" fontWeight="600" color="primary.main" sx={{ ml: 1 }}>
                                ₹{routeTotal.toFixed(2)}
                              </Typography>
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Date &amp; Time</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rows.map((tx) => {
                                    const date = tx.paymentCollectedAt ?? tx.collectedAt ?? tx.createdAt;
                                    const amount = tx.amount != null ? Number(tx.amount) : 0;
                                    const customer = tx.customerName || tx.customerPhone || '—';
                                    return (
                                      <TableRow key={tx.id} hover>
                                        <TableCell>{formatCashCollectionDate(date)}</TableCell>
                                        <TableCell>{tx.orderId || '—'}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>₹{amount.toFixed(2)}</TableCell>
                                        <TableCell>{customer}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRiderDetails}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Riders;
